import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
    AvatarGroup,
    AvatarGroupItem,
    AvatarGroupPopover,
    Link,
    makeStyles,
    mergeClasses,
    partitionAvatarGroupItems,
    Text,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { bundleIcon, DeleteFilled, DeleteRegular, EditFilled, EditRegular } from '@fluentui/react-icons';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { useAccess } from '../../../../auth/useAccess';
import { Column, Paths, Row } from '../../../common';
import { PartyControl } from '../../../controls/PartyControl';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomDataTableProps,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { Case, CaseStatus, UserRef } from '../../../../data/types';
import { useCaseDataService } from '../../../../data/services';
import { useUtilities } from '../../../../utilities/useUtilities';
import { useMediaSizes } from '../../../common/Styles/useMediaSizes';

enum ColumnName {
    clientIcon = 'clientIcon',
    caseName = 'name',
    caseType = 'caseType',
    proceeding = 'proceeding',
    overview = 'overview',
    retainedBy = 'retainedBy',
    milestone = 'milestone',
    assignedTo = 'assignedTo',
    status = 'status',
    actions = 'actions',
}

const useStyles = makeStyles({
    assignedToText: {
        overflow: 'hidden',
        maxWidth: '240px',
        display: 'block',
    },
    link: {
        color: tokens.colorBrandForegroundLink,
    },
});

type CasesTableProps = Partial<CustomDataTableProps<Case>> & {
    cases: Case[];
    deleteCase?: (caseObj: Case) => void;
    showDescription?: boolean;
    showOverview?: boolean;
    showRetainedBy?: boolean;
    showMilestone?: boolean;
    showAssignedTo?: boolean;
    showStatus?: boolean;
    showActions?: boolean;
    showCaseType?: boolean;
    showClientIcon?: boolean;
};
export const CasesTable = (props: CasesTableProps) => {
    const {
        cases = [],
        deleteCase,
        showCaseType = false,
        showDescription = false,
        showOverview = false,
        showRetainedBy = false,
        showMilestone = false,
        showStatus = false,
        showAssignedTo = false,
        showActions = false,
        showClientIcon = false,
        ...otherProps
    } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const caseDataService = useCaseDataService();
    const access = useAccess(primaryAccount?.user);
    const navigate = useNavigate();
    const utils = useUtilities();
    const mediaSizes = useMediaSizes();

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [actionCase, setActionCase] = useState<Case>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const linkStyles = mergeClasses(commonStyles.button.root, styles.link);

    //UI Events
    const editCase = (caseId?: string) => {
        if (!!caseId) {
            navigate(Paths.viewEditCase(caseId));
        }
    };
    const clickDelete = (caseObj: Case) => {
        !!deleteCase && deleteCase(caseObj);
    };

    //Columns and Actions
    const columns: CustomDataTableColumn<Case>[] = [
        {
            id: ColumnName.clientIcon,
            header: '',
            renderCell: (caseObj) => {
                return <PartyControl size={'icon'} party={caseObj?.retainedBy?.party} />;
            },
            show: showClientIcon,
            pointerEvents: true,
            defaultWidth: 35,
            minWidth: 35,
            primary: showClientIcon,
        },
        {
            id: ColumnName.caseName,
            header: 'Case',
            compare: (a, b) => {
                return a.caseName?.localeCompare(b.caseName || '') || 0;
            },
            renderCell: (caseObj) => {
                return (
                    <Column noGap>
                        <Link className={linkStyles} onClick={() => editCase(caseObj.caseId)}>
                            <Text>{caseObj.caseName}</Text>
                        </Link>
                        {!showCaseType && (
                            <Text size={200}>{caseObj?.caseType}</Text>
                        )}
                    </Column>
                );
            },
            pointerEvents: true,
            defaultWidth: Number(mediaSizes.getSizes(300, 275, 250, 200)),
            minWidth: 100,
            primary: !showClientIcon,
        },
        {
            id: ColumnName.caseType,
            header: 'Case Type',
            renderCell: (caseObj) => {
                return <Text>{caseObj?.caseType}</Text>;
            },
            show: showCaseType,
            defaultWidth: 150,
            minWidth: mediaSizes.isMobile ? 75 : 100,
        },
        {
            id: ColumnName.proceeding,
            header: 'Description',
            compare: (a, b) => {
                return a.shortDescription?.fieldValue?.localeCompare(b.shortDescription?.fieldValue || '') || 0;
            },
            renderCell: (caseObj) => {
                return <Text>{caseObj?.shortDescription?.fieldValue}</Text>;
            },
            show: showDescription,
            defaultWidth: Number(mediaSizes.getSizes(275, 230, 200, 160)),
            minWidth: Number(mediaSizes.getSizes(200, 175, 150, 125)),
        },
        {
            id: ColumnName.overview,
            header: 'Overview',
            renderCell: (caseObj) => {
                return <Text>{caseObj?.overview}</Text>;
            },
            show: showOverview,
            defaultWidth: Number(mediaSizes.getSizes(450, 350, 280, 220)),
            minWidth: Number(mediaSizes.getSizes(200, 175, 150, 125)),
        },
        {
            id: ColumnName.retainedBy,
            header: 'Retained By',
            compare: (a, b) => {
                return a.retainedBy?.party?.name?.localeCompare(b.retainedBy?.party?.name || '') || 0;
            },
            renderCell: (caseObj) => {
                return <PartyControl size={mediaSizes.isMobile ? 'icon' : 'option'} party={caseObj?.retainedBy?.party} />;
            },
            show: showRetainedBy,
            pointerEvents: true,
            defaultWidth: Number(mediaSizes.getSizes(250, 200, 160, 130)),
            minWidth: mediaSizes.isMobile ? 75 : 125,
        },
        {
            id: ColumnName.assignedTo,
            header: 'Assigned To',
            cellFocusMode: 'group',
            pointerEvents: true,
            renderCell: (caseObj) => {
                const { inlineItems, overflowItems } = partitionAvatarGroupItems({
                    items: (caseObj?.assignedUsers as UserRef[]) || [],
                    layout: 'pie',
                });
                const getUsersName = (userId: string) => utils.getUsersName(userId, primaryAccount?.accountUsers || []);
                return (
                    <Row gap='medium' noWrap>
                        <Column width='auto' vAlign='middle'>
                            <AvatarGroup layout='pie' className={commonStyles.button.root}>
                                {inlineItems.map((user) => (
                                    <AvatarGroupItem
                                        name={getUsersName(user.userId)}
                                        key={user.userId}
                                        className={commonStyles.button.root}
                                    />
                                ))}
                                {overflowItems && (
                                    <AvatarGroupPopover>
                                        {overflowItems.map((user) => (
                                            <AvatarGroupItem name={getUsersName(user.userId)} key={user.userId} />
                                        ))}
                                    </AvatarGroupPopover>
                                )}
                            </AvatarGroup>
                        </Column>
                        <Column width='auto' vAlign='middle' padding='pr-4'>
                            <Text truncate size={200} wrap={false} className={styles.assignedToText}>
                                {caseObj.assignedUsers?.map((x) => getUsersName(x.userId)).join(', ')}
                            </Text>
                        </Column>
                    </Row>
                );
            },
            show: showAssignedTo,
            defaultWidth: Number(mediaSizes.getSizes(200, 180, 150, 130)),
            minWidth: mediaSizes.isMobile ? 100 : 125,
        },
        {
            id: ColumnName.status,
            header: 'Status',
            compare: (a, b) => {
                const aStatus = a?.status?.status;
                const bStatus = b?.status?.status;
                return aStatus?.localeCompare(bStatus || '') || 0;
            },
            renderCell: (caseObj) => {
                const updatedCaseStatus = caseDataService.getCaseStatus(caseObj.status as CaseStatus);
                return (
                    <>
                        {!!updatedCaseStatus?.description ? (
                            <Tooltip content={updatedCaseStatus?.description} relationship='description'>
                                <Text>{`${caseObj?.status?.status || ''}`}</Text>
                            </Tooltip>
                        ) : (
                            <Text>{`${caseObj?.status?.status || ''}`}</Text>
                        )}
                    </>
                );
            },
            show: showStatus,
            defaultWidth: Number(mediaSizes.getSizes(200, 160, 140, 120)),
            minWidth: mediaSizes.isMobile ? 90 : 110,
            pointerEvents: true,
        },
    ];

    const EditBundle = bundleIcon(EditFilled, EditRegular);
    const DeleteBundle = bundleIcon(DeleteFilled, DeleteRegular);

    const actions: CustomDataTableAction<Case>[] = [
        {
            id: 'edit',
            ariaLabel: 'Edit',
            canAccess: (caseObj) => access.cases.canEditCaseInfo(caseObj?.caseId) || false,
            icon: <EditBundle />,
            label: 'Edit',
            tooltip: 'Edit',
            onClick: (caseObj) => editCase(caseObj.caseId as string),
            onMenuClick: (caseObj) => {
                caseObj && editCase(caseObj?.caseId as string);
            },
        },
        {
            id: 'delete',
            ariaLabel: 'Delete',
            canAccess: () => access.account.canDeleteCases(),
            icon: <DeleteBundle />,
            label: 'Delete',
            tooltip: 'Delete',
            singleSelectItemOnClick: true,
            onMenuClick: (caseObj) => {
                setActionCase(caseObj);
                setDelConfirmOpen(true);
            },
            renderDialog: (caseObj, children) => {
                return (
                    <ConfirmationDialog
                        title='Confirm Delete'
                        icon={<DeleteRegular />}
                        text={`Are you sure you want to delete case ${caseObj?.caseName}?`}
                        onConfirm={() => clickDelete(caseObj)}
                    >
                        {children}
                    </ConfirmationDialog>
                );
            },
        },
    ];

    return (
        <Column align='center'>
            <CustomDataTable<Case>
                items={cases}
                rowId='caseId'
                columns={columns}
                actions={actions}
                select={false}
                onDoubleClickItem={(item: Case) => editCase(item.caseId)}
                showActions={showActions}
                {...otherProps}
            />
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete case ${actionCase?.caseName}?`}
                open={delConfirmOpen}
                onOpenChange={(isOpen) => {
                    setDelConfirmOpen(isOpen as boolean);
                    setActionCase(undefined);
                }}
                onConfirm={() => actionCase && clickDelete(actionCase)}
            />
        </Column>
    );
};
